// src/components/CreditSuggestions.js

import {
  Alert,
  Box,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { Panel } from "primereact/panel";
import { useEffect, useState } from "react";
import Styles from "../../styles/_global/Styles";
import LiquidGlass from "../LiquidGlass"; // add this import

export default function CreditSuggestions({ userUuid, profileLoaded }) {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState({
    products: [],
    behaviours: [],
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const alertGlass = isMobile
    ? {
        backgroundColor: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#fff",
        textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
        "& .MuiAlert-icon": { color: "#fff" },
      }
    : {};

  useEffect(() => {
    if (!profileLoaded) return;
    let cancelled = false;

    (async function fetchSuggestions() {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const apiKey = process.env.REACT_APP_BACKEND_API_KEY;
        const res = await axios.get(
          `${backendUrl}/api/credit-suggestions/${userUuid}`,
          { headers: { "x-api-key": apiKey } }
        );
        if (!cancelled && res.data.length) {
          setSuggestions(res.data[0].suggestions);
        }
      } catch (err) {
        console.error("Error fetching suggestions:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userUuid, profileLoaded]);

  return (
    <Box>
      <Box>
        <LiquidGlass
          altComponent={Panel}
          title="Potential Next Steps"
          icon="pi pi-sparkles"
          sx={{ padding: 1 }}
          altProps={{
            header: (
              <>
                <i className="pi pi-sparkles" /> Potential Next Steps
              </>
            ),
          }}
        >
          {loading ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={alertGlass}>
                Based on your most recent credit report data, here are some
                steps you might consider to strengthen your credit profile and
                financial well-being.
              </Alert>

              <Grid
                container
                spacing={2}
                sx={{
                  p: 2,
                  justifyContent: isMobile ? "flex-start" : "space-between",
                }}
              >
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: isMobile ? "#fff" : "inherit",
                    }}
                  >
                    Solutions
                  </Typography>
                  <List dense>
                    {suggestions.products.map((p, i) => (
                      <ListItem
                        key={i}
                        alignItems="flex-start"
                        sx={{
                          mb: 2,
                          borderLeft: {
                            borderColor: isMobile
                              ? "rgba(255,255,255,0.2)"
                              : "#eee",
                            borderLeftWidth: 4,
                            pl: 2,
                            borderLeftStyle: "solid",
                          },
                        }} // bottom margin
                      >
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                mb: 0.5,
                                color: isMobile ? "#fff" : "inherit",
                              }}
                            >
                              {p.title}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  display: "block",
                                  mb: 1,
                                  color: isMobile ? "#fff" : "inherit",
                                }}
                              >
                                {p.explanation}
                              </Typography>
                              {p.link && (
                                <Box sx={{ mt: 1 }}>
                                  <Link
                                    href={p.link}
                                    rel="noopener"
                                    underline="none"
                                    sx={{
                                      mt: { xs: 3, md: 0 },
                                      fontSize: {
                                        xs: "0.8rem",
                                        sm: "0.8rem",
                                        md: "0.875rem",
                                      },
                                      fontWeight: { xs: 600, sm: 600, md: 500 },
                                      color: isMobile
                                        ? "#fff"
                                        : `${Styles.primary.main} !important`,
                                      textShadow: isMobile
                                        ? "0px 1px 2px rgba(0,0,0,0.5)"
                                        : "none",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      ...(isMobile && {
                                        backgroundColor: Styles.primary.main,
                                        px: 2,
                                        py: 1,
                                        borderRadius: "4px",
                                      }),
                                    }}
                                  >
                                    <i className="pi pi-angle-right" /> View
                                    Solution(s)
                                  </Link>
                                </Box>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: isMobile ? "#fff" : "inherit",
                    }}
                  >
                    Actions
                  </Typography>
                  <List dense>
                    {suggestions.behaviours.map((b, i) => (
                      <ListItem
                        key={i}
                        alignItems="flex-start"
                        sx={{
                          mb: 1,
                          color: isMobile ? "#fff" : "inherit",
                          borderLeft: {
                            borderColor: isMobile
                              ? "rgba(255,255,255,0.2)"
                              : "#eee",
                            borderLeftWidth: 4,
                            pl: 2,
                            borderLeftStyle: "solid",
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, mb: 0.5 }}
                            >
                              {b.title}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              component="span"
                              variant="body2"
                              color="inherit"
                            >
                              {b.explanation}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                {suggestions.summary && (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: isMobile ? "#fff" : "inherit",
                      }}
                    >
                      Summary
                    </Typography>
                    {typeof suggestions.summary === "string" ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: isMobile ? "#fff" : "inherit",
                          whiteSpace: "pre-wrap",
                        }}
                        dangerouslySetInnerHTML={{
                          __html:suggestions.summary,
                        }}
                      />
                    ) : (
                      suggestions.summary
                    )}
                  </Grid>
                )}
              </Grid>
            </>
          )}

          <Divider
            sx={{
              my: 1,
              borderColor: isMobile ? "rgba(255,255,255,0.2)" : "#eee",
            }}
          />

          <Box sx={{ p: 2 }}>
            <Typography
              variant="caption"
              color={isMobile ? "#fff" : "#444"}
              sx={{
                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                textShadow: isMobile ? "0px 1px 2px rgba(0,0,0,0.5)" : "none",
              }}
            >
              We use Artificial Intelligence to determine personalised,
              data-driven next steps tailored to your unique credit profile.
              None of your personally identifiable information is shared during
              this process. The suggestions or recommendations above are for
              informational purposes only and do not constitute financial
              advice. To make informed decisions, please consult with a
              qualified financial advisor.
            </Typography>
          </Box>
        </LiquidGlass>
      </Box>
    </Box>
  );
}
